import { type JSONContent } from "@tiptap/core";
import { z } from "zod";
import { validateNoteLinkHref } from "./sanitize";

// -----------------------------------------------------------------------------
// Tiptap node schemas
// -----------------------------------------------------------------------------
// Whitelist every node type the editor actually produces. Unknown keys on
// attrs objects are silently stripped (zod's default `.strip()` behavior) so
// docs carrying extras like `textAlign` still parse, but the stripped values
// never flow into `generateHTML` output that reaches the DOM (workspace
// render, PDF-export iframe). `.passthrough()` would reopen that XSS surface.

const LINK_TARGET_ALLOWLIST = ["_self", "_blank", "_parent", "_top"] as const;
const LINK_REL_TOKEN_ALLOWLIST = new Set([
  "noopener",
  "noreferrer",
  "nofollow",
  "ugc",
  "sponsored",
]);

const blockAttrsSchema = z.object({
  blockId: z.string().min(1).nullable().optional(),
});

// Enforce the URL scheme allowlist at the schema boundary by delegating to
// `validateNoteLinkHref` (declared below — hoisted, so the closure is safe at
// parse time). `sanitizeNode` stays as a secondary drop-and-preserve-text
// fallback for any docs that slip past this gate.
const linkHrefSchema = z.string().refine(
  (href) => validateNoteLinkHref(href).ok,
  { message: "Link href uses a disallowed scheme or is malformed" }
);

// Restrict `rel` to a closed token set. Input is a space-separated list —
// empty strings pass through as-is and every token must be on the allowlist.
const linkRelSchema = z
  .string()
  .refine((rel) => {
    const tokens = rel.trim().split(/\s+/).filter(Boolean);
    return tokens.every((token) => LINK_REL_TOKEN_ALLOWLIST.has(token));
  }, { message: "Link rel contains a disallowed token" });

const tiptapMarkSchema = z.discriminatedUnion("type", [
  z.object({ type: z.literal("bold") }),
  z.object({ type: z.literal("italic") }),
  z.object({ type: z.literal("strike") }),
  z.object({ type: z.literal("code") }),
  z.object({
    type: z.literal("link"),
    // `class` is not in the allowlist: it is a pure render-layer attr and
    // accepting arbitrary strings would let a crafted notes doc inject class
    // names into the rendered DOM. Default `.strip()` drops it silently.
    attrs: z.object({
      href: linkHrefSchema,
      target: z.enum(LINK_TARGET_ALLOWLIST).nullable().optional(),
      rel: linkRelSchema.nullable().optional(),
    }),
  }),
]);

const tiptapInlineNodeSchema = z.discriminatedUnion("type", [
  z.object({
    type: z.literal("text"),
    // ProseMirror forbids empty text nodes — generateHTML throws a
    // RangeError on one, so reject them at the parse boundary instead.
    text: z.string().min(1),
    marks: z.array(tiptapMarkSchema).optional(),
  }),
  z.object({ type: z.literal("hardBreak") }),
]);

// Single reviewable site for the Zod-narrow → Tiptap-wide structural-type
// bridge. Zod's literal inference produces `{ type: "paragraph"; ... }` but
// Tiptap's `JSONContent` interface declares `type?: string`, so direct
// assignment fails at the variance boundary. Centralizing the cast in one
// helper (rather than sprinkling `as unknown as z.ZodType<JSONContent>` at
// every recursive site) accomplishes three things:
//   1. Only one grep target if the bridge ever needs to become `z.custom`
//      or a nominal type.
//   2. Any future recursive sub-schema picks up the typing pattern
//      uniformly without tempting a reach for `z.any()` when the compiler
//      complains.
//   3. Runtime parsing remains fully structural — this helper is erased at
//      compile time.
// Do NOT swap for `z.custom<JSONContent>()`: that drops the inner
// discriminated-union validation and reopens the `z.any()` XSS hole this
// schema exists to close.
function asJSONContentLazy(factory: () => z.ZodTypeAny): z.ZodType<JSONContent> {
  return z.lazy(factory) as unknown as z.ZodType<JSONContent>;
}

export const tiptapBlockNodeSchema: z.ZodType<JSONContent> = asJSONContentLazy(() =>
  z.discriminatedUnion("type", [
    z.object({
      type: z.literal("paragraph"),
      attrs: blockAttrsSchema.optional(),
      content: z.array(tiptapInlineNodeSchema).optional(),
    }),
    z.object({
      type: z.literal("heading"),
      attrs: blockAttrsSchema
        .extend({ level: z.number().int().min(1).max(4) })
        .optional(),
      content: z.array(tiptapInlineNodeSchema).optional(),
    }),
    z.object({
      type: z.literal("codeBlock"),
      attrs: blockAttrsSchema
        .extend({ language: z.string().nullable().optional() })
        .optional(),
      content: z.array(tiptapInlineNodeSchema).optional(),
    }),
    z.object({
      type: z.literal("blockquote"),
      attrs: blockAttrsSchema.optional(),
      content: z.array(tiptapBlockNodeSchema).optional(),
    }),
    z.object({
      type: z.literal("bulletList"),
      attrs: blockAttrsSchema.optional(),
      content: z.array(tiptapListItemSchema).optional(),
    }),
    z.object({
      type: z.literal("orderedList"),
      attrs: blockAttrsSchema
        .extend({ start: z.number().int().optional() })
        .optional(),
      content: z.array(tiptapListItemSchema).optional(),
    }),
    z.object({
      type: z.literal("taskList"),
      attrs: blockAttrsSchema.optional(),
      content: z.array(tiptapTaskItemSchema).optional(),
    }),
    z.object({
      type: z.literal("horizontalRule"),
      attrs: blockAttrsSchema.optional(),
    }),
  ])
);

const tiptapListItemSchema: z.ZodType<JSONContent> = asJSONContentLazy(() =>
  z.object({
    type: z.literal("listItem"),
    attrs: blockAttrsSchema.optional(),
    content: z.array(tiptapBlockNodeSchema).optional(),
  })
);

const tiptapTaskItemSchema: z.ZodType<JSONContent> = asJSONContentLazy(() =>
  z.object({
    type: z.literal("taskItem"),
    attrs: z
      .object({
        blockId: z.string().min(1).nullable().optional(),
        checked: z.boolean().optional(),
      })
      .optional(),
    content: z.array(tiptapBlockNodeSchema).optional(),
  })
);

export const notesDocumentSchema = z.object({
  type: z.literal("doc"),
  content: z.array(tiptapBlockNodeSchema).optional().default([]),
});

